import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, of, switchMap } from 'rxjs';
import { CompanyContextService } from '@core/context/company-context.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import {
  DashboardPaymentMethod,
  DashboardPaymentSummaryItem,
  DashboardProductRankingItem,
} from '@models/dashboard.interface';
import { ICashSessionPaymentMethodsSummary } from '@models/cashSession.interface';
import { CashSessionService } from '@modules/cash/cash-session/cash-session.service';
import { DashboardService } from '@modules/home/dashboard.service';

const STOCK_ALERT_ROLES = new Set(['ADMIN', 'EMPLOYEE']);

const PAYMENT_CARD_METHODS: DashboardPaymentMethod[] = [
  'card',
  'debit_card',
  'credit_card',
];

const PAYMENT_DISPLAY_ORDER: DashboardPaymentMethod[] = [
  'cash',
  'card',
  'transfers',
  'other',
];

function normalizePayments(
  dashboardPayments: DashboardPaymentSummaryItem[],
  cashSummary: ICashSessionPaymentMethodsSummary | null,
): DashboardPaymentSummaryItem[] {
  const totals = new Map<DashboardPaymentMethod, number>();
  const labels = new Map<DashboardPaymentMethod, string>();

  for (const payment of dashboardPayments) {
    const method = PAYMENT_CARD_METHODS.includes(payment.paymentMethod)
      ? 'card'
      : payment.paymentMethod;
    const total = Number(payment.total ?? 0);

    totals.set(method, Number(totals.get(method) ?? 0) + total);

    if (!labels.has(method) && payment.label?.trim()) {
      labels.set(method, payment.label.trim());
    }
  }

  const cardTotalFromCashSummary = Number(
    (cashSummary?.payment_methods.debit_card.expected_amount ?? 0) +
      (cashSummary?.payment_methods.credit_card.expected_amount ?? 0),
  );

  if (!totals.has('card') && cardTotalFromCashSummary > 0) {
    totals.set('card', cardTotalFromCashSummary);
  }

  if (!totals.has('cash')) {
    const cashTotal = Number(cashSummary?.payment_methods.cash.expected_amount ?? 0);
    if (cashTotal > 0) {
      totals.set('cash', cashTotal);
    }
  }

  if (!totals.has('transfers')) {
    const transferTotal = Number(
      cashSummary?.payment_methods.transfers.expected_amount ?? 0,
    );
    if (transferTotal > 0) {
      totals.set('transfers', transferTotal);
    }
  }

  if (!totals.has('other')) {
    const otherTotal = Number(cashSummary?.payment_methods.other.expected_amount ?? 0);
    if (otherTotal > 0) {
      totals.set('other', otherTotal);
    }
  }

  return PAYMENT_DISPLAY_ORDER
    .map((paymentMethod) => {
      const total = Number(totals.get(paymentMethod) ?? 0);

      if (total <= 0) {
        return null;
      }

      return {
        paymentMethod,
        label: labels.get(paymentMethod) ?? getPaymentMethodLabel(paymentMethod),
        total,
      } satisfies DashboardPaymentSummaryItem;
    })
    .filter((payment): payment is DashboardPaymentSummaryItem => payment !== null);
}

function getPaymentMethodLabel(paymentMethod: DashboardPaymentMethod): string {
  if (paymentMethod === 'cash') return 'Efectivo';
  if (paymentMethod === 'card') return 'Tarjetas';
  if (paymentMethod === 'transfers') return 'Transferencias';
  if (paymentMethod === 'other') return 'Otros';
  if (paymentMethod === 'debit_card') return 'Tarjeta de débito';
  if (paymentMethod === 'credit_card') return 'Tarjeta de crédito';

  return 'Pago';
}

function isNamedProduct(item: DashboardProductRankingItem): boolean {
  const name = String(item.productName ?? '').trim();
  return Number(item.productBusinessId) > 0 && !!name && name.toLocaleLowerCase('es') !== 'producto sin nombre';
}

function normalizeProductRankingItem(
  item: DashboardProductRankingItem,
): DashboardProductRankingItem {
  return {
    ...item,
    productName: String(item.productName ?? '').trim() || 'Producto sin nombre',
    quantitySold: Number.isFinite(Number(item.quantitySold))
      ? Number(item.quantitySold)
      : 0,
    salesAmount: Number.isFinite(Number(item.salesAmount))
      ? Number(item.salesAmount)
      : 0,
  };
}

@Component({
  selector: 'app-home',
  imports: [CurrencyPipe, DecimalPipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  private readonly dashboardService = inject(DashboardService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly cashSessionService = inject(CashSessionService);
  private readonly tenantSessionService = inject(TenantSessionService);

  readonly canViewStockAlerts = computed(() =>
    this.tenantSessionService.getCurrentUser()?.rolesObject?.some((role) => {
      const key = String(role?.role_key ?? role?.catalogRoleObject?.role_key ?? '')
        .trim()
        .toUpperCase();
      return STOCK_ALERT_ROLES.has(key);
    }) ?? false,
  );
  private readonly company = toSignal(this.companyContextService.company$, {
    initialValue: this.companyContextService.value,
  });
  private readonly businessId = computed(() =>
    Number(this.company()?.business_id ?? 0) || null,
  );
  private readonly cashSummary = toSignal(
    toObservable(this.businessId).pipe(
      distinctUntilChanged(),
      switchMap((businessId) => {
        if (!businessId) {
          return of(null as ICashSessionPaymentMethodsSummary | null);
        }

        return this.cashSessionService.getCurrentCashSessionPaymentMethods().pipe(
          catchError(() => of(null as ICashSessionPaymentMethodsSummary | null)),
        );
      }),
    ),
    { initialValue: null as ICashSessionPaymentMethodsSummary | null },
  );

  readonly dashboardState = this.dashboardService.state;
  readonly dashboard = computed(() => this.dashboardState().data);
  readonly payments = computed(
    () => normalizePayments(this.dashboard()?.paymentSummary ?? [], this.cashSummary()),
  );
  readonly stockLists = computed(() => ({
    lowStock: this.dashboard()?.inventoryAlerts.lowStock ?? [],
    overstock: this.dashboard()?.inventoryAlerts.overstock ?? [],
  }));
  readonly topProducts = computed(() => ({
    bestSellers: (this.dashboard()?.topProducts.bestSellers ?? []).filter(isNamedProduct).map(
      normalizeProductRankingItem,
    ),
    leastSellers: (this.dashboard()?.topProducts.leastSellers ?? []).filter(isNamedProduct).map(
      normalizeProductRankingItem,
    ),
  }));
  readonly thermometer = computed(() => {
    const data = this.dashboard()?.thermometer;
    const todayTotal = Number(data?.todayTotal ?? 0);
    const referenceAverage = Number(data?.referenceAverage ?? 0);
    const differenceAmount = todayTotal - referenceAverage;
    const hasReference = referenceAverage > 0;
    const isEqual = hasReference && Math.abs(differenceAmount) < 0.01;

    return {
      todayTotal,
      referenceAverage,
      hasReference,
      comparisonTitle: !hasReference
        ? 'Pronto podrás comparar tus ventas'
        : isEqual
          ? 'Hoy llevas lo mismo que tu promedio diario'
          : differenceAmount > 0
            ? 'Hoy llevas más ventas que tu promedio diario'
            : 'Hoy llevas menos ventas que tu promedio diario',
    };
  });

  constructor() {
    effect(() => {
      const businessId = this.businessId();

      if (!businessId) {
        return;
      }

      void this.dashboardService.fetch(String(businessId));
    });
  }
}
